import React, { useContext, useState } from "react";
import { useQuery } from "@apollo/react-hooks";
import { Check, Minus, MoreVertical } from "react-feather";
import InviteSchoolAdministrator from "./InviteSchoolAdministrator";
import Header from "../../../components/Header";
import BackLink from "../../../components/BackLink";
import { AuthContext } from "../../../services/user/AuthProvider";
import { GET_FRANCHISE_SCHOOL_ADMINS } from "../services/graphql";
import styles from "../Franchises.module.scss";

const FranchiseSchoolAdmins = ({ schoolId, franchiseId, portal = false }) => {
  const { currentUser } = useContext(AuthContext);
  const [showInvite, setShowInvite] = useState(false);
  const [refreshError, setRefreshError] = useState("");
  const { data, loading, error, refetch } = useQuery(GET_FRANCHISE_SCHOOL_ADMINS, {
    variables: { schoolId },
    fetchPolicy: "no-cache"
  });
  const school = data && data.school;
  const belongsToFranchise = school && school.franchise && String(school.franchise.id) === String(franchiseId);
  const admins = (school && school.admins) || [];
  const schoolsPath = portal ? `/franchise-portal/${franchiseId}/schools` : `/franchises/${franchiseId}/schools`;

  return (
    <div className={`${styles.franchises} ${portal ? styles.portal : ""}`}>
      {portal && <Header user={currentUser} portalFranchise={belongsToFranchise ? school.franchise : null} portalFranchiseId={franchiseId} portalPage="schools" />}
      <BackLink text={belongsToFranchise ? school.name : "School"} to={`${schoolsPath}/${schoolId}`} />
      {loading && <p role="status">Loading school administrators...</p>}
      {error && <p role="alert">Unable to load school administrators. Please try again later.</p>}
      {!loading && !error && !belongsToFranchise && <p>School not found in this franchise.</p>}
      {!loading && !error && belongsToFranchise && <>
        <div className={`${styles.detailHeader} ${styles.header}`}>
          <div>
            <h1 className="title">School administrators</h1>
            <p>Control who can access and manage this school’s BoomReader account.</p>
          </div>
          <button type="button" className={styles.primaryButton} onClick={() => setShowInvite(true)}>Invite admin</button>
        </div>

        {refreshError && <p role="alert">{refreshError}</p>}
        {showInvite && <InviteSchoolAdministrator school={school} closeModal={() => setShowInvite(false)} onInvited={() => {
          setShowInvite(false);
          setRefreshError("");
          refetch().catch(() => setRefreshError("Invitation sent, but the administrator list could not be refreshed. Please reload the page."));
        }} />}

        <div className={styles.adminAccountsSummary}>
          <strong>{school.administratorAccountsCount || 0}</strong>
          <div>
            <h3>administrator accounts</h3>
            <p>{school.administratorAccountsSummary || "—"}</p>
          </div>
        </div>

        <div className={styles.section}>
          <h2>Active administrators</h2>
          {admins.length === 0 ? <p>No school administrators.</p> : <ul className={styles.schoolAdminList}>
            {admins.map(admin => {
              const name = `${admin.firstName || ""} ${admin.lastName || ""}`.trim();
              const isPrimary = school.primaryAdministrator && String(school.primaryAdministrator.id) === String(admin.id);
              return (
                <li key={admin.id} data-status={(admin.status || "").toLowerCase()}>
                  <div className={styles.schoolAdminIdentity}>
                    <span className={styles.addIcon}>{admin.initials || `${(admin.firstName || "").charAt(0)}${(admin.lastName || "").charAt(0)}` || "—"}</span>
                    <div>
                      <h3>{name || "—"}</h3>
                      <p>{admin.roleTitle || (isPrimary ? "Primary administrator" : "School administrator")} · {admin.email || "—"}</p>
                    </div>
                  </div>
                  <p>{admin.activityLabel || "—"}</p>
                  <strong className={styles.schoolAdminStatus}>{isPrimary ? "Primary" : admin.statusLabel || "—"}</strong>
                  <button type="button" className={styles.schoolAdminMenu} aria-label={`Manage ${name || "administrator"}`} disabled><MoreVertical size={20} /></button>
                </li>
              );
            })}
          </ul>}
        </div>

        <div className={styles.section}>
          <h2>School administrator permissions</h2>
          <div className={`${styles.detailPanel} ${styles.adminPermissions}`}>
            <div><span><Check size={16} /></span><div><h3>Manage pupils and classes</h3><p>Add, edit and archive pupil/class records.</p></div></div>
            <div><span><Check size={16} /></span><div><h3>Manage staff and parents</h3><p>Invite users and maintain account access.</p></div></div>
            <div><span><Check size={16} /></span><div><h3>View school reading data</h3><p>Access only this school’s reading information.</p></div></div>
            <div><span className={styles.adminPermissionRestricted}><Minus size={16} /></span><div><h3>Cannot access franchise settings</h3><p>No access to other {school.franchise.name} schools or Boom staff tools.</p></div></div>
          </div>
        </div>
        <div className={styles.adminAccountNotice}>
          <h3>Account changes are recorded</h3>
          <p>Invitations, removals and role changes appear in the school activity history.</p>
        </div>
      </>}
    </div>
  );
};

export default FranchiseSchoolAdmins;
